// src/components/SuggerimentiOperativiWidget.js
// Riquadro Dashboard "💡 Suggerimenti Operativi" — riusa esclusivamente dati già esistenti
// per suggerire il passo successivo più logico, senza popup né notifiche invasive.
import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useVedeTutteFatturazioni } from "../hooks/useCanSee";
import { percentualeRichiestaCommessa } from "../utils/fatturazioneCommessa";
import { valutaSegnaliCommessa, sommaCostiOda, noleggiDaDecidere, noleggiRettificati } from "../utils/cabinaDiRegia";
import { toLocalDateStr } from "../utils/dateUtils";
import { ricaviCommessa, costiDaMovimenti, costiCalcolatiDaBudget, budgetPrincipale } from "../utils/economiaCommessa";

const FREQUENZE_GIORNI = { settimanale:7, mensile:30, trimestrale:90, semestrale:180, annuale:365 };

function prossimaScadenza(piano, ultimoIntervento) {
  const base = ultimoIntervento?.data ? new Date(ultimoIntervento.data+"T00:00:00")
    : piano.decorrenza ? new Date(piano.decorrenza+"T00:00:00")
    : (piano.createdAt?.toDate ? piano.createdAt.toDate() : new Date());
  const giorni = FREQUENZE_GIORNI[piano.frequenza] || 30;
  const next = new Date(base); next.setDate(next.getDate()+giorni);
  return next;
}

// "Bassa" rinominata in "Informativi": sono situazioni da tenere d'occhio, non urgenze —
// chiamarle "priorità bassa" suggeriva un'azione mancata, non un semplice promemoria.
const PRIORITA_ORDINE = { alta:0, media:1, bassa:2 };
const PRIORITA_STILE = {
  alta:  { col:"#dc2626", pallino:"🔴", label:"Alta" },
  media: { col:"#d97706", pallino:"🟡", label:"Media" },
  bassa: { col:"#2563eb", pallino:"🔵", label:"Informativi" },
};
const CATEGORIE = {
  commesse:     { icona:"📄", label:"Commesse" },
  fatturazione: { icona:"💰", label:"Fatturazione" },
  magazzino:    { icona:"📦", label:"Magazzino" },
  segnalazioni: { icona:"📣", label:"Segnalazioni" },
  manutenzione: { icona:"🔧", label:"Manutenzione" },
};
const ORDINE_CATEGORIE = ["commesse","fatturazione","magazzino","segnalazioni","manutenzione"];
const LIMITE_DEFAULT = 10;

export default function SuggerimentiOperativiWidget({ currentUser, isOrgAdmin, isPlatformOwner, effectiveOrgId, onNavigate, compatto = false }) {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reportCommessa, setReportCommessa] = useState([]);
  const [richiesteFatturazione, setRichiesteFatturazione] = useState([]);
  const [manutenzionePiani, setManutenzionePiani] = useState([]);
  const [manutenzioneInterventi, setManutenzioneInterventi] = useState([]);
  const [segnalazioniCliente, setSegnalazioniCliente] = useState([]);
  const [nuoveForniture, setNuoveForniture] = useState([]);
  const [richiesteMateriali, setRichiesteMateriali] = useState([]);
  const [cronoprogrammaFasi, setCronoprogrammaFasi] = useState([]);
  // Aggiunti per riusare le regole condivise con la Cabina di Regia, senza duplicarle
  const [computiSAL, setComputiSAL] = useState([]);
  const [budgetCommesse, setBudgetCommesse] = useState([]);
  const [movimentiEconomici, setMovimentiEconomici] = useState([]);
  const [ordiniAcquisto, setOrdiniAcquisto] = useState([]);
  const [attivitaExtra, setAttivitaExtra] = useState([]);
  const [noleggi, setNoleggi] = useState([]);
  const [espanso, setEspanso] = useState(false);

  useEffect(() => {
    if (!effectiveOrgId) return;
    const unsubs = [
      onSnapshot(query(collection(db,"projects"), where("organizationId","==",effectiveOrgId)),
        snap => setProjects(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.isParent && !p.archived))),
      onSnapshot(query(collection(db,"tasks"), where("organizationId","==",effectiveOrgId)),
        snap => setTasks(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"reportCommessa"), where("orgId","==",effectiveOrgId)),
        snap => setReportCommessa(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"manutenzionePiani"), where("organizationId","==",effectiveOrgId)),
        snap => setManutenzionePiani(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",effectiveOrgId)),
        snap => setManutenzioneInterventi(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"segnalazioniCliente"), where("orgId","==",effectiveOrgId)),
        snap => setSegnalazioniCliente(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"nuoveForniture"), where("organizationId","==",effectiveOrgId)),
        snap => setNuoveForniture(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"richiesteMateriali"), where("organizationId","==",effectiveOrgId)),
        snap => setRichiesteMateriali(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"cronoprogrammaFasi"), where("orgId","==",effectiveOrgId)),
        snap => setCronoprogrammaFasi(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"computiSAL"), where("orgId","==",effectiveOrgId)),
        snap => setComputiSAL(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"budgetCommessa"), where("organizationId","==",effectiveOrgId)),
        snap => setBudgetCommesse(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"movimentiEconomici"), where("organizationId","==",effectiveOrgId), where("tipo","==","costo")),
        snap => setMovimentiEconomici(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"ordiniAcquisto"), where("organizationId","==",effectiveOrgId)),
        snap => setOrdiniAcquisto(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"attivitaExtra"), where("orgId","==",effectiveOrgId)),
        snap => setAttivitaExtra(snap.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(collection(db,"noleggi"), where("orgId","==",effectiveOrgId)),
        snap => setNoleggi(snap.docs.map(d=>({id:d.id,...d.data()}))), () => setNoleggi([])),
    ];
    return () => unsubs.forEach(u=>u());
  }, [effectiveOrgId]);

  // Richieste di fatturazione: tutte per chi le gestisce, le proprie per il PM (regole 57);
  // i totali della commessa per il PM vengono dal budget (utils/fatturazioneCommessa.js)
  const vedeTutte = useVedeTutteFatturazioni();
  useEffect(() => {
    if (!effectiveOrgId || vedeTutte === null || !currentUser?.uid) return;
    const filtri = [where("organizationId","==",effectiveOrgId)];
    if (!vedeTutte) filtri.push(where("richiedenteUid","==",currentUser.uid));
    return onSnapshot(query(collection(db,"richiesteFatturazione"), ...filtri),
      snap => setRichiesteFatturazione(snap.docs.map(d=>({id:d.id,...d.data()}))), () => setRichiesteFatturazione([]));
  }, [effectiveOrgId, vedeTutte, currentUser?.uid]);
  const budgetDi = id => budgetPrincipale(budgetCommesse.filter(b => b.projectId === id));

  const isAdmin = isOrgAdmin || isPlatformOwner;
  const mieProject = isAdmin ? projects : projects.filter(p => p.responsabileUid === currentUser?.uid);
  const mieProjectIds = new Set(mieProject.map(p=>p.id));

  const suggerimenti = [];
  const nomeCommessa = p => `${p.codiceCommessa ? `[${p.codiceCommessa}] ` : ""}${p.name}`;

  // 1) Commessa chiusa senza Report di Chiusura
  mieProject.filter(p => p.statoCommessa === "chiusa").forEach(p => {
    const haReportChiusura = reportCommessa.some(r => r.projectId === p.id && r.tipo === "chiusura");
    if (!haReportChiusura) {
      suggerimenti.push({
        id:`rc-${p.id}`, entityId:p.id, categoria:"commesse", priorita:"alta", giorni:0,
        titolo: nomeCommessa(p),
        azione: "Report di Chiusura da verificare",
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:p.id, project:p }),
      });
    }
  });

  // 2) Cronoprogramma al 100% (tutte le attività completate/annullate) e nessuna richiesta di fatturazione al 100%
  mieProject.filter(p => p.statoCommessa !== "chiusa").forEach(p => {
    const pt = tasks.filter(t => t.projectId === p.id);
    if (pt.length === 0) return;
    const tutteFatte = pt.every(t => t.status === "completata" || t.status === "annullata");
    if (!tutteFatte) return;
    const percFatturato = percentualeRichiestaCommessa(p.id, richiesteFatturazione, budgetDi(p.id), vedeTutte);
    if (percFatturato < 99.99) {
      suggerimenti.push({
        id:`fatt100-${p.id}`, entityId:p.id, categoria:"fatturazione", priorita:"media", giorni:0,
        titolo: nomeCommessa(p),
        azione: "Cronoprogramma al 100% — verifica Richiesta di Fatturazione",
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:p.id, project:p }),
      });
    }
  });

  // 3) Manutenzione in scadenza o scaduta
  manutenzionePiani.filter(piano => mieProjectIds.has(piano.projectId)).forEach(piano => {
    const interventiPiano = manutenzioneInterventi.filter(iv => iv.pianoId === piano.id).sort((a,b)=>(b.data||"").localeCompare(a.data||""));
    const ultimo = interventiPiano[0] || null;
    const scadenza = prossimaScadenza(piano, ultimo);
    const oggi = new Date();
    const giorniMancanti = Math.ceil((scadenza - oggi) / (1000*60*60*24));
    const progetto = projects.find(p=>p.id===piano.projectId);
    if (giorniMancanti < 0) {
      suggerimenti.push({
        id:`manut-${piano.id}`, categoria:"manutenzione", priorita:"alta", giorni:Math.abs(giorniMancanti),
        titolo: `${piano.nome}${progetto?` — ${nomeCommessa(progetto)}`:""}`,
        azione: `Manutenzione scaduta da ${Math.abs(giorniMancanti)} giorn${Math.abs(giorniMancanti)===1?"o":"i"}`,
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:piano.projectId, project:progetto }),
      });
    } else if (giorniMancanti <= 7) {
      suggerimenti.push({
        id:`manut-${piano.id}`, categoria:"manutenzione", priorita:"media", giorni:7-giorniMancanti,
        titolo: `${piano.nome}${progetto?` — ${nomeCommessa(progetto)}`:""}`,
        azione: `Manutenzione in scadenza tra ${giorniMancanti} giorn${giorniMancanti===1?"o":"i"}`,
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:piano.projectId, project:progetto }),
      });
    }
  });

  // 4) Commessa non aggiornata da tempo (14 giorni)
  mieProject.filter(p => p.statoCommessa !== "chiusa").forEach(p => {
    const riferimento = p.updatedAt?.toDate ? p.updatedAt.toDate() : (p.createdAt?.toDate ? p.createdAt.toDate() : null);
    if (!riferimento) return;
    const giorniFermi = Math.floor((new Date() - riferimento) / (1000*60*60*24));
    if (giorniFermi >= 14) {
      suggerimenti.push({
        id:`ferma-${p.id}`, entityId:p.id, categoria:"commesse", priorita:"bassa", giorni:giorniFermi,
        titolo: nomeCommessa(p),
        azione: `Nessun aggiornamento da ${giorniFermi} giorni`,
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:p.id, project:p }),
      });
    }
  });

  // 5-6) Segnalazioni Cliente
  const mieSegnalazioni = isAdmin ? segnalazioniCliente
    : segnalazioniCliente.filter(s => s.assegnatarioUid===currentUser?.uid || s.compilatoreUid===currentUser?.uid);
  const oggiStr = toLocalDateStr(new Date());
  mieSegnalazioni.filter(s => s.stato!=="chiusa" && s.dataScadenza && s.dataScadenza < oggiStr).forEach(s => {
    const giorni = Math.floor((new Date() - new Date(s.dataScadenza+"T00:00:00")) / (1000*60*60*24));
    suggerimenti.push({
      id:`segscad-${s.id}`, categoria:"segnalazioni", priorita:"alta", giorni,
      titolo: `${s.numero} — ${s.clienteNome||"—"}`,
      azione: `Scadenza superata da ${giorni} giorn${giorni===1?"o":"i"}`,
      azioneLabel: "Apri Registro",
      onClick: () => onNavigate && onNavigate({ page:"registro-segnalazioni" }),
    });
  });
  mieSegnalazioni.filter(s => s.stato!=="chiusa" && !s.assegnatarioUid).forEach(s => {
    const creata = s.createdAt?.toDate ? s.createdAt.toDate() : null;
    if (!creata) return;
    const giorni = Math.floor((new Date() - creata) / (1000*60*60*24));
    if (giorni >= 3) {
      suggerimenti.push({
        id:`segassenz-${s.id}`, categoria:"segnalazioni", priorita:"media", giorni,
        titolo: `${s.numero} — ${s.clienteNome||"—"}`,
        azione: `Senza assegnatario da ${giorni} giorni`,
        azioneLabel: "Apri Registro",
        onClick: () => onNavigate && onNavigate({ page:"registro-segnalazioni" }),
      });
    }
  });

  // 7-9-10-11-12) Regole condivise con la Cabina di Regia — stessa logica, stesso posto,
  // per ogni commessa dell'utente. Non ripete "Segnalazione senza seguito": quella ha già
  // una logica più specifica poco sopra in questo stesso widget.
  mieProject.filter(p => p.statoCommessa !== "chiusa").forEach(p => {
    const ctx = {
      tasks: tasks.filter(t=>t.projectId===p.id),
      hasCronoprogramma: cronoprogrammaFasi.some(f=>f.projectId===p.id),
      computoSAL: computiSAL.find(s=>s.projectId===p.id) || null,
      // Stesse regole del Controllo Economico (utils/economiaCommessa.js): budget principale
      // della commessa e fonte delle ore con cui il Controllo Economico ha calcolato i costi (W450)
      budget: ricaviCommessa(budgetDi(p.id), movimentiEconomici.filter(m=>m.projectId===p.id)),
      costiSostenuti: costiDaMovimenti(movimentiEconomici.filter(m=>m.projectId===p.id), budgetDi(p.id)?.costiCalcolati?.fonteOre) + sommaCostiOda(ordiniAcquisto, p.id)
        + costiCalcolatiDaBudget(budgetDi(p.id)),
      segnalazioni: [], // esclusa qui di proposito, gestita altrove in questo widget
      attivitaExtra: attivitaExtra.filter(e=>e.projectId===p.id),
      noleggiDaDecidere: noleggiDaDecidere(noleggi.filter(n=>n.projectId===p.id), undefined, isAdmin),
      noleggiRettificati: noleggiRettificati(noleggi.filter(n=>n.projectId===p.id), isAdmin),
    };
    const { segnali } = valutaSegnaliCommessa(p, ctx);
    segnali.forEach(s => {
      const priorita = s.gravita==="alta" ? "alta" : s.gravita==="media" ? "media" : "bassa";
      suggerimenti.push({
        id:`${s.id}-${p.id}`, entityId:p.id, categoria:"commesse", priorita, giorni:s.giorni||0,
        titolo: nomeCommessa(p),
        azione: `${s.label} — ${s.dettaglio}`,
        azioneLabel: "Apri Cabina di Regia",
        // si apre direttamente la Cabina di Regia della commessa, dove l'avviso ha il suo «Vai»
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:p.id, project:p, initialTab:"cabina-regia" }),
      });
    });
  });

  // 8) Commessa aperta da più di 7 giorni senza nessuna attività creata
  mieProject.filter(p => p.statoCommessa !== "chiusa").forEach(p => {
    const apertura = p.createdAt?.toDate ? p.createdAt.toDate() : null;
    if (!apertura) return;
    const giorni = Math.floor((new Date() - apertura) / (1000*60*60*24));
    if (giorni < 7) return;
    const haAttivita = tasks.some(t => t.projectId === p.id);
    if (!haAttivita) {
      suggerimenti.push({
        id:`noattivita-${p.id}`, entityId:p.id, categoria:"commesse", priorita:"media", giorni,
        titolo: nomeCommessa(p),
        azione: `Aperta da ${giorni} giorni, nessuna attività creata`,
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:p.id, project:p }),
      });
    }
  });


  // 10) NF ferma in bozza ("Da inviare") da più di 5 giorni
  nuoveForniture.filter(nf => (nf.statoRichiesta||"Da inviare")==="Da inviare" && mieProjectIds.has(nf.commessaId)).forEach(nf => {
    const creata = nf.createdAt?.toDate ? nf.createdAt.toDate() : null;
    if (!creata) return;
    const giorni = Math.floor((new Date() - creata) / (1000*60*60*24));
    if (giorni >= 5) {
      suggerimenti.push({
        id:`nf-${nf.id}`, categoria:"magazzino", priorita:"media", giorni,
        titolo: nf.commessaNome||"—",
        azione: `NF ferma in bozza da ${giorni} giorni`,
        azioneLabel: "Apri Richiedi NF",
        onClick: () => onNavigate && onNavigate({ page:"richiedi-nf" }),
      });
    }
  });

  // 10bis) NF inviata dal PM ma ancora nessuno dell'ufficio Acquisti l'ha presa in carico
  // da più di 3 giorni lavorativi — diverso dal punto sopra (quello riguarda una bozza mai
  // nemmeno inviata dal PM, questo una richiesta già arrivata ad Acquisti e lì rimasta ferma).
  nuoveForniture.filter(nf => nf.statoRichiesta==="Inviata" && !nf.presaInCaricoDa && !nf.annullata && mieProjectIds.has(nf.commessaId)).forEach(nf => {
    // Preferisce inviataAt (il momento vero in cui è arrivata ad Acquisti) — createdAt resta
    // solo come ripiego per le NF create prima che questo campo esistesse, altrimenti una
    // bozza tenuta ferma giorni dal PM mostrerebbe un'urgenza falsata non attribuibile ad Acquisti.
    const riferimento = nf.inviataAt?.toDate ? nf.inviataAt.toDate() : (nf.createdAt?.toDate ? nf.createdAt.toDate() : null);
    if (!riferimento) return;
    const giorni = Math.floor((new Date() - riferimento) / (1000*60*60*24));
    if (giorni >= 3) {
      suggerimenti.push({
        id:`nf-noncarico-${nf.id}`, categoria:"magazzino", priorita: giorni>=7?"alta":"media", giorni,
        titolo: nf.commessaNome||"—",
        azione: `NF #${nf.numero||""} inviata da ${giorni} giorni, ancora nessuno l'ha presa in gestione`,
        azioneLabel: "Apri Centro Acquisti",
        onClick: () => onNavigate && onNavigate({ page:"centro-acquisti" }),
      });
    }
  });

  // 11) Richiesta di Fatturazione mai inviata su commessa già chiusa
  mieProject.filter(p => p.statoCommessa === "chiusa").forEach(p => {
    const nonInviateMie = richiesteFatturazione.filter(f => f.projectId === p.id && f.stato === "da_fatturare").length;
    const nonInviate = vedeTutte ? nonInviateMie : Math.max(nonInviateMie, budgetDi(p.id)?.fatturazioneCalcolata?.nDaFatturare || 0);
    if (nonInviate > 0) {
      suggerimenti.push({
        id:`fattchiusa-${p.id}`, entityId:p.id, categoria:"fatturazione", priorita:"alta", giorni:0,
        titolo: nomeCommessa(p),
        azione: "Chiusa con richiesta di fatturazione non ancora lavorata",
        azioneLabel: "Apri Commessa",
        onClick: () => onNavigate && onNavigate({ page:"project", projectId:p.id, project:p }),
      });
    }
  });

  // 12) Richiesta al Magazzino in attesa da più di 5 giorni
  richiesteMateriali.filter(r => r.stato === "in_attesa" && mieProjectIds.has(r.projectId)).forEach(r => {
    const creata = r.createdAt?.toDate ? r.createdAt.toDate() : null;
    if (!creata) return;
    const giorni = Math.floor((new Date() - creata) / (1000*60*60*24));
    if (giorni >= 5) {
      suggerimenti.push({
        id:`magaz-${r.id}`, categoria:"magazzino", priorita:"media", giorni,
        titolo: r.projectName||"—",
        azione: `Richiesta in attesa da ${giorni} giorni`,
        azioneLabel: "Apri Ordini Materiali",
        onClick: () => onNavigate && onNavigate({ page:"magazzino" }),
      });
    }
  });

  // Ordinamento: prima priorità, poi — a parità di priorità — quanto è più "vecchio/urgente"
  suggerimenti.sort((a,b) => PRIORITA_ORDINE[a.priorita] - PRIORITA_ORDINE[b.priorita] || b.giorni - a.giorni);

  const conteggi = { alta:0, media:0, bassa:0 };
  suggerimenti.forEach(s => conteggi[s.priorita]++);

  // Raggruppa per categoria + entityId (stessa commessa) PRIMA di applicare il limite: così
  // "primi 10" conta situazioni/card reali, non righe — coerente con quello che vede l'utente.
  const cardKey = s => `${s.categoria}::${s.entityId || s.id}`;
  const cardOrdine = []; // ordine di prima comparsa, rispetta già l'ordinamento per priorità/giorni
  const cardMappa = {};
  suggerimenti.forEach(s => {
    const k = cardKey(s);
    if (!cardMappa[k]) { cardMappa[k] = []; cardOrdine.push(k); }
    cardMappa[k].push(s);
  });
  const tutteLeCard = cardOrdine.map(k => cardMappa[k]);
  const cardVisibili = espanso ? tutteLeCard : tutteLeCard.slice(0, LIMITE_DEFAULT);
  const totaleCard = tutteLeCard.length;

  const gruppi = ORDINE_CATEGORIE
    .map(cat => ({ cat, cards: cardVisibili.filter(items => items[0].categoria === cat) }))
    .filter(g => g.cards.length > 0);

  // Dissolvenza quando un suggerimento si risolve: lo teniamo a video ancora un istante,
  // sfumato, invece di farlo sparire di scatto — nessuna nuova logica, solo transizione.
  const idsAttuali = new Set(suggerimenti.map(s=>s.id));
  const [mostrati, setMostrati] = useState(suggerimenti);
  const prevIds = useRef(new Set());
  useEffect(() => {
    const risolti = [...prevIds.current].filter(id => !idsAttuali.has(id));
    if (risolti.length === 0) {
      setMostrati(suggerimenti);
    } else {
      setMostrati(prev => {
        const rimastiEFading = prev
          .filter(s => idsAttuali.has(s.id) || risolti.includes(s.id))
          .map(s => idsAttuali.has(s.id) ? s : { ...s, _fading:true });
        const nuovi = suggerimenti.filter(s => !prev.some(p=>p.id===s.id));
        return [...rimastiEFading, ...nuovi];
      });
      setTimeout(() => setMostrati(suggerimenti), 400);
    }
    prevIds.current = idsAttuali;
  }, [suggerimenti.map(s=>s.id).join(",")]);

  // Home (ottobre 2026): una riga sola con il conteggio; l'elenco completo è nel Centro Operativo
  if (compatto) {
    const alte = suggerimenti.filter(s => s.priorita === "alta").length;
    return <RigaRiepilogo icona="💡" colore={alte ? "#dc2626" : suggerimenti.length ? "#d97706" : "#059669"}
      testo={suggerimenti.length === 0 ? "Nessun suggerimento operativo sulle commesse" : `${suggerimenti.length} suggeriment${suggerimenti.length === 1 ? "o" : "i"} operativ${suggerimenti.length === 1 ? "o" : "i"} sulle commesse${alte ? ` · ${alte} ad alta priorità` : ""}`}
      onApri={() => onNavigate && onNavigate({ page: "control-center" })}/>;
  }

  return (
    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20, marginBottom:24 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
        <h3 style={{ fontSize:15, fontWeight:700, margin:0 }}>💡 Suggerimenti Operativi</h3>
        <span style={{ fontSize:10, color:"#059669", fontWeight:600 }}>🟢 Aggiornato in tempo reale</span>
      </div>

      {suggerimenti.length === 0 ? (
        <div style={{ fontSize:13, color:"#059669", fontWeight:600, textAlign:"center", padding:"12px 0" }}>
          ✅ Nessun suggerimento operativo. Tutte le attività risultano aggiornate.
        </div>
      ) : (
        <>
          <div style={{ display:"flex", alignItems:"center", gap:14, margin:"10px 0 16px", fontSize:12, color:"#6b7280" }}>
            <span>{suggerimenti.length} suggeriment{suggerimenti.length===1?"o":"i"}</span>
            {conteggi.alta>0 && <span style={{fontWeight:700, color:PRIORITA_STILE.alta.col}}>🔴 {conteggi.alta} Alta</span>}
            {conteggi.media>0 && <span style={{fontWeight:700, color:PRIORITA_STILE.media.col}}>🟡 {conteggi.media} Media</span>}
            {conteggi.bassa>0 && <span style={{fontWeight:700, color:PRIORITA_STILE.bassa.col}}>🔵 {conteggi.bassa} Informativi</span>}
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {gruppi.map(g => (
              <div key={g.cat}>
                <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>
                  {CATEGORIE[g.cat].icona} {CATEGORIE[g.cat].label}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {g.cards.map(items => {
                    const capofila = items[0];
                    const st = PRIORITA_STILE[items.reduce((peggio,it)=>PRIORITA_ORDINE[it.priorita]<PRIORITA_ORDINE[peggio.priorita]?it:peggio, capofila).priorita];
                    const mostrato = mostrati.find(m=>m.id===capofila.id) || capofila;
                    return (
                      <div key={capofila.id} style={{
                        display:"flex", alignItems:"center", gap:10,
                        borderLeft:`3px solid ${st.col}`, background:"#fafafa", borderRadius:6,
                        padding:"7px 10px", opacity: mostrato._fading?0:1, transition:"opacity .4s ease" }}>
                        <div style={{ minWidth:0, flex:1 }}>
                          <div style={{ fontSize:12.5, fontWeight:700, color:"#111827" }}>{capofila.titolo}</div>
                          {items.map(it => (
                            <div key={it.id} style={{ fontSize:11.5, color:"#6b7280" }}>
                              {items.length>1 && <span style={{color:PRIORITA_STILE[it.priorita].col}}>{PRIORITA_STILE[it.priorita].pallino} </span>}
                              {it.azione}
                            </div>
                          ))}
                        </div>
                        <button onClick={capofila.onClick}
                          style={{ flexShrink:0, fontSize:11, fontWeight:700, color:"#7c3aed", background:"#faf5ff",
                            border:"1px solid #e9d5ff", borderRadius:7, padding:"5px 10px", cursor:"pointer", whiteSpace:"nowrap" }}>
                          {capofila.azioneLabel} →
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {!espanso && totaleCard > LIMITE_DEFAULT && (
            <div style={{ textAlign:"center", marginTop:14 }}>
              <button onClick={()=>setEspanso(true)}
                style={{ fontSize:12, fontWeight:700, color:"#7c3aed", background:"none", border:"none", cursor:"pointer" }}>
                Visualizza tutti ({totaleCard}) →
              </button>
            </div>
          )}
          {espanso && totaleCard > LIMITE_DEFAULT && (
            <div style={{ textAlign:"center", marginTop:14 }}>
              <button onClick={()=>setEspanso(false)}
                style={{ fontSize:12, fontWeight:700, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
                Mostra meno ↑
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Riga di riepilogo per la Home: testo e collegamento al Centro Operativo. */
export function RigaRiepilogo({ icona, testo, colore = "#7c3aed", onApri }) {
  return (
    <button onClick={onApri}
      style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"10px 14px", marginBottom:10, background:"#fff",
        border:"1px solid #e5e7eb", borderLeft:`4px solid ${colore}`, borderRadius:10, cursor:"pointer", textAlign:"left", fontFamily:"'Inter',sans-serif" }}>
      <span style={{ fontSize:16 }}>{icona}</span>
      <span style={{ flex:1, fontSize:13, fontWeight:600, color:"#111827" }}>{testo}</span>
      <span style={{ fontSize:12, fontWeight:700, color:"#7c3aed", whiteSpace:"nowrap" }}>Centro Operativo ›</span>
    </button>
  );
}
